import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * SELETOR DE TURMA COM ENTURMAÇÃO — sugere e explica; nunca decide.
 *
 * O `<select>` que existia listava **todas** as turmas em ordem alfabética, sem
 * idade, sem vaga e sem turno: dava para pôr um bebê de 6 meses na pré-escola e
 * nada reclamava. Aqui a lista chega **ordenada pela que mais serve**, cada
 * opção marcada, e o motivo aparece embaixo em português.
 *
 * ⚖️ **Toda turma continua selecionável, inclusive a incompatível.** A creche
 * tem exceções que o sistema não conhece — irmãos na mesma sala, laudo, decisão
 * pedagógica. O sistema mostra o que sabe e registra a escolha dela.
 * Ver `backend/src/turmas/enturmacao.regras.ts`.
 */

export type Aderencia = 'compativel' | 'atencao' | 'incompativel' | 'nao_avaliavel';

export interface ObservacaoEnturmacao {
  codigo: string;
  severidade: 'alerta' | 'atencao' | 'informativo';
  mensagem: string;
  base_legal?: string;
}

export interface SugestaoEnturmacao {
  id_turma: number;
  nome: string;
  turno: string;
  aderencia: Aderencia;
  resumo: string;
  observacoes: ObservacaoEnturmacao[];
  idade_minima_meses: number | null;
  idade_maxima_meses: number | null;
  ociosas: number | null;
  capacidade_efetiva: number | null;
}

export interface ResultadoEnturmacao {
  ano_letivo: number;
  idade_meses_no_corte: number | null;
  idade_meses_hoje: number | null;
  etapa: string | null;
  sugestoes: SugestaoEnturmacao[];
  observacoes: ObservacaoEnturmacao[];
}

export const MARCA: Record<Aderencia, { icone: string; label: string; bg: string; cor: string }> = {
  compativel: { icone: '✓', label: 'serve', bg: '#ecfdf5', cor: '#047857' },
  atencao: { icone: '⚠', label: 'atenção', bg: '#fffbeb', cor: '#b45309' },
  nao_avaliavel: { icone: '?', label: 'sem dados para conferir', bg: '#f1f5f9', cor: '#475569' },
  incompativel: { icone: '✕', label: 'fora da faixa etária', bg: '#fef2f2', cor: '#b91c1c' },
};

const COR_SEVERIDADE: Record<string, string> = {
  alerta: '#b91c1c',
  atencao: '#b45309',
  informativo: '#475569',
};

/** "1 ano e 3 meses" — mesma leitura do backend, para a tela não inventar outra. */
export function idadeLegivel(meses: number | null | undefined): string {
  if (meses == null || !Number.isFinite(meses) || meses < 0) return 'idade desconhecida';
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const parteAnos = anos > 0 ? `${anos} ano${anos > 1 ? 's' : ''}` : '';
  const parteMeses = resto > 0 ? `${resto} ${resto > 1 ? 'meses' : 'mês'}` : '';
  if (parteAnos && parteMeses) return `${parteAnos} e ${parteMeses}`;
  return parteAnos || parteMeses || 'recém-nascido';
}

const ETAPA_LEGIVEL: Record<string, string> = {
  creche: 'creche (0 a 3 anos)',
  pre_escola: 'pré-escola (4 e 5 anos)',
  fora_educacao_infantil: 'idade de Ensino Fundamental',
};

interface Props {
  /** `YYYY-MM-DD`. Sem ela o sistema não afirma qual turma serve. */
  dataNascimento?: string | null;
  turnoPretendido?: string | null;
  valor: number | null;
  onChange: (idTurma: number | null, sugestao: SugestaoEnturmacao | null) => void;
  /** Rótulo da primeira opção quando nada está escolhido. */
  textoVazio?: string;
  compacto?: boolean;
}

export default function SeletorDeTurma({
  dataNascimento,
  turnoPretendido,
  valor,
  onChange,
  textoVazio = 'Escolha a turma…',
  compacto = false,
}: Props) {
  const [resultado, setResultado] = useState<ResultadoEnturmacao | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Espera o gestor terminar de digitar a data antes de perguntar ao servidor —
  // sem isso, cada tecla do campo de data vira uma requisição.
  useEffect(() => {
    let cancelado = false;
    const timer = setTimeout(async () => {
      setCarregando(true);
      setErro(null);
      try {
        const params = new URLSearchParams();
        if (dataNascimento) params.set('data_nascimento', dataNascimento);
        if (turnoPretendido) params.set('turno', turnoPretendido);
        const { data } = await api.get<ResultadoEnturmacao>(
          `/turmas/enturmacao?${params.toString()}`,
        );
        if (!cancelado) setResultado(data);
      } catch (e: any) {
        if (!cancelado) {
          setErro(e?.response?.data?.message || 'Não foi possível carregar a sugestão de turmas.');
        }
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }, 350);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [dataNascimento, turnoPretendido]);

  const sugestoes = resultado?.sugestoes ?? [];
  const escolhida = sugestoes.find((s) => s.id_turma === valor) ?? null;

  // Quando a turma já vem escolhida de fora (a "turma pretendida" da fila), o
  // `onChange` nunca dispara e quem chamou ficaria sem saber se aquela turma
  // serve — inclusive na hora de avisar que está fora da faixa. Ao terminar de
  // carregar, devolvemos a avaliação da turma já selecionada.
  // Depende só de `resultado`: não entra em laço porque a lista só muda quando
  // a consulta muda.
  useEffect(() => {
    if (valor == null || !resultado) return;
    const s = resultado.sugestoes.find((x) => x.id_turma === valor) ?? null;
    if (s) onChange(valor, s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultado]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!compacto && (
        <div style={{ fontSize: 13, color: '#475569' }}>
          {carregando && 'Consultando as turmas…'}
          {!carregando && !dataNascimento && (
            <span style={{ color: '#b45309' }}>
              Informe a data de nascimento para o sistema sugerir a turma.
            </span>
          )}
          {!carregando && dataNascimento && resultado && (
            <>
              No corte de 31/03/{resultado.ano_letivo} a criança tinha{' '}
              <b>{idadeLegivel(resultado.idade_meses_no_corte)}</b>
              {resultado.etapa && ETAPA_LEGIVEL[resultado.etapa]
                ? ` — ${ETAPA_LEGIVEL[resultado.etapa]}.`
                : '.'}
            </>
          )}
        </div>
      )}

      <select
        value={valor ?? ''}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : null;
          onChange(id, sugestoes.find((s) => s.id_turma === id) ?? null);
        }}
        style={{
          borderRadius: 8,
          border: '1px solid #cbd5e1',
          padding: '9px 10px',
          fontSize: 14,
          width: '100%',
          background: '#fff',
        }}
      >
        <option value="">{textoVazio}</option>
        {sugestoes.map((s) => (
          <option key={s.id_turma} value={s.id_turma}>
            {MARCA[s.aderencia].icone} {s.nome} · {s.turno}
            {s.ociosas != null ? ` · ${s.ociosas} vaga${s.ociosas === 1 ? '' : 's'}` : ''}
          </option>
        ))}
      </select>

      {erro && <div style={{ fontSize: 13, color: '#b91c1c' }}>{erro}</div>}

      {!carregando && !erro && sugestoes.length === 0 && (
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Nenhuma turma cadastrada — cadastre as salas e as turmas antes de enturmar.
        </div>
      )}

      {/* Por que esta turma serve (ou não). A escolha continua sendo da creche. */}
      {escolhida && (
        <div
          style={{
            background: MARCA[escolhida.aderencia].bg,
            border: `1px solid ${MARCA[escolhida.aderencia].cor}22`,
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, color: MARCA[escolhida.aderencia].cor }}>
            {MARCA[escolhida.aderencia].icone} {MARCA[escolhida.aderencia].label}
          </div>
          {/* A observação de idade já carrega o resumo inteiro (ela precisa ser
              autoexplicativa para quem lê a API). Aqui na tela, imprimir os dois
              faz a mesma frase aparecer duas vezes seguidas. */}
          {!escolhida.observacoes.some((o) => o.mensagem.includes(escolhida.resumo.trim())) && (
            <div style={{ color: '#334155', marginTop: 4 }}>{escolhida.resumo}</div>
          )}
          {escolhida.observacoes.map((o) => (
            <div key={o.codigo} style={{ marginTop: 6, color: COR_SEVERIDADE[o.severidade] }}>
              • {o.mensagem}
              {o.base_legal && (
                <div style={{ color: '#64748b', fontSize: 12 }}>{o.base_legal}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Observações da criança (não de uma turma): idade que mudou no ano, etc. */}
      {resultado?.observacoes?.map((o) => (
        <div key={o.codigo} style={{ fontSize: 12, color: COR_SEVERIDADE[o.severidade] }}>
          {o.mensagem}
        </div>
      ))}
    </div>
  );
}
